// Review-feed continuation for the transcript Label inspector. `feed` carries
// the review route's own search string; only this helper interprets it to
// derive the matching central Answer-list key and generated-client request.
// Keyset tokens are passed back verbatim and are never decoded or synthesized.

import type { InfiniteData, QueryClient } from "@tanstack/react-query";

import type { components } from "../api/schema";
import { markerScoreRequest, reviewCalls, reviewCell } from "./markerReview";
import { apiClient } from "./apiClient";
import { ApiRequestError } from "./errors";
import { DEFAULT_PAGE_SIZE } from "./pagination";
import { queryKeys } from "./queryKeys";
import type { ListQueryFilters, ListQueryPage } from "./useListQuery";

type Answer = components["schemas"]["AnswerResponse"];
type AnswerPage = ListQueryPage<"/v1/answers">;
type AnswerFilters = ListQueryFilters<"/v1/answers">;
// Continuation navigation needs only the existing Answer coordinates.
export type ReviewAnswer = Pick<Answer, "id" | "transcript_id" | "marker_id">;
type ReviewPage = {
  items: ReviewAnswer[];
  next_page_token: string | null;
};
type AnswerPages = InfiniteData<AnswerPage, string | undefined>;

const EVAL_STATUSES = ["failed", "not_eligible", "ok"] as const;

export interface ReviewFeedSearch {
  agentId?: string;
  eval_status?: string;
  flagged?: boolean;
  marker_id?: string;
  needs_review?: boolean | "any";
  reviewed?: boolean;
  marker_revision_id?: string;
  origin?: "live" | "simulated";
  batch_id?: string;
  agent_version_id?: string;
  created_after?: string;
  created_before?: string;
  sort?: "oldest" | "newest";
  source?: "human" | "machine";
  teamId?: string;
}

export function encodeReviewFeedSearch(scope: ReviewFeedSearch): string {
  const search = new URLSearchParams();
  if (scope.agentId) {
    search.set("agent", scope.agentId);
  } else if (scope.teamId) {
    search.set("team", scope.teamId);
  }
  if (scope.source) {
    search.set("source", scope.source);
  }
  if (scope.eval_status) {
    search.set("eval_status", scope.eval_status);
  }
  if (scope.flagged !== undefined) {
    search.set("flagged", String(scope.flagged));
  }
  if (scope.reviewed !== undefined) {
    search.set("reviewed", String(scope.reviewed));
  }
  const needsReview =
    scope.source === undefined &&
    scope.needs_review !== false &&
    scope.needs_review !== "any";
  search.set("needs_review", needsReview ? "true" : "any");
  search.set("sort", scope.sort ?? (needsReview ? "oldest" : "newest"));
  for (const key of [
    "marker_revision_id",
    "origin",
    "batch_id",
    "agent_version_id",
    "created_after",
    "created_before",
  ] as const) {
    if (scope[key]) search.set(key, scope[key]);
  }
  if (scope.marker_id) {
    search.set("marker_id", scope.marker_id);
  }
  return search.toString();
}

export function reviewFeedFilters(feed: string): AnswerFilters {
  const search = new URLSearchParams(feed);
  const agentId = text(search.get("agent"));
  const teamId = agentId === undefined ? text(search.get("team")) : undefined;
  const source = search.get("source");
  const statuses = search
    .getAll("eval_status")
    .flatMap((value) => value.split(","))
    .filter((value): value is (typeof EVAL_STATUSES)[number] =>
      EVAL_STATUSES.includes(value as (typeof EVAL_STATUSES)[number]),
    );
  return {
    agent_id: agentId,
    evaluation_status: statuses.length > 0 ? statuses : undefined,
    sort:
      search.get("sort") === "oldest"
        ? "oldest"
        : search.get("sort") === "newest"
          ? "newest"
          : search.get("needs_review") === "true"
            ? "oldest"
            : "newest",
    marker_revision_id: text(search.get("marker_id"))
      ? text(search.get("marker_revision_id"))
      : undefined,
    origin:
      search.get("origin") === "live"
        ? "live"
        : search.get("origin") === "simulated"
          ? "simulated"
          : undefined,
    batch_id:
      search.get("origin") === "simulated"
        ? text(search.get("batch_id"))
        : undefined,
    agent_version_id: text(search.get("agent_version_id")),
    created_after: text(search.get("created_after")),
    created_before: text(search.get("created_before")),
    flagged: booleanValue(search.get("flagged")),
    marker_id: text(search.get("marker_id")),
    needs_review:
      search.get("needs_review") === "true" &&
      source !== "human" &&
      source !== "machine"
        ? true
        : undefined,
    reviewed: booleanValue(search.get("reviewed")),
    review_routed: booleanValue(search.get("review_routed")),
    source: source === "human" || source === "machine" ? source : undefined,
    team_id: teamId,
    transcript_id: text(search.get("transcript_id")),
  };
}

function text(value: string | null): string | undefined {
  return value == null || value.length === 0 ? undefined : value;
}

function booleanValue(value: string | null): boolean | undefined {
  if (value === "true") {
    return true;
  }
  if (value === "false") {
    return false;
  }
  return undefined;
}

async function fetchAnswerPage(
  filters: AnswerFilters,
  pageToken: string | undefined,
): Promise<AnswerPage> {
  const { data, error } = await apiClient.GET("/v1/answers", {
    params: {
      query: {
        ...filters,
        page_size: DEFAULT_PAGE_SIZE,
        page_token: pageToken,
      },
    },
  });
  if (error) {
    throw new ApiRequestError(error);
  }
  return data;
}

// A selection is transient navigation state, independent of refetched lists.
// Keeping its order prevents a save/SSE invalidation from moving the anchor.
export class ReviewSelection {
  private items: ReviewAnswer[] = [];
  private token: string | undefined;
  private initialized = false;
  private loading: Promise<void> | undefined;
  private readonly aliases = new Map<string, string>();
  private readonly saved = new Set<string>();

  constructor(
    private readonly client: QueryClient,
    readonly feed: string,
  ) {}

  get labelsSaved(): number {
    return this.saved.size;
  }

  position(answerId: string): string {
    const index = this.index(answerId);
    // ASSUMED(Show a lower bound until the keyset selection's last page.)
    const total = this.token
      ? `at least ${String(this.items.length)}`
      : String(this.items.length);
    return index < 0 ? "Selection loading" : `${String(index + 1)} of ${total}`;
  }

  hasPrevious(answerId: string): boolean {
    return this.index(answerId) > 0;
  }

  recordSave(originalId: string, createdId: string): void {
    const anchor = this.aliases.get(originalId) ?? originalId;
    this.aliases.set(originalId, anchor);
    this.aliases.set(createdId, anchor);
    this.saved.add(createdId);
  }

  private index(answerId: string): number {
    const original = this.aliases.get(answerId) ?? answerId;
    return this.items.findIndex((item) => item.id === original);
  }

  async initialize(answerId: string, transcriptId?: string): Promise<void> {
    const cached = reviewCell(this.feed)
      ? undefined
      : this.client.getQueryData<AnswerPages>(
          queryKeys.answers.list(reviewFeedFilters(this.feed)),
        );
    const newlySelected =
      this.index(answerId) < 0 &&
      cached?.pages.some((page) =>
        page.items.some((item) => item.id === answerId),
      );
    // A fresh row chosen after returning to Review uses that feed's current
    // window. Already-known anchors remain stable through invalidation.
    if (!this.initialized || newlySelected) {
      if (cached) {
        this.items = this.calls(cached.pages.flatMap((page) => page.items));
        this.token = cached.pages.at(-1)?.next_page_token ?? undefined;
        this.initialized = true;
      } else {
        await this.loadPage();
      }
    }
    const coordinate = () =>
      reviewFeedFilters(this.feed).marker_id
        ? this.items.find((item) => item.transcript_id === transcriptId)
        : undefined;
    while (this.index(answerId) < 0 && !coordinate() && this.token) {
      await this.loadPage();
    }
    const anchor = coordinate();
    if (this.index(answerId) < 0 && anchor) {
      this.aliases.set(answerId, anchor.id);
    }
  }

  private calls(items: ReviewAnswer[]): ReviewAnswer[] {
    return reviewFeedFilters(this.feed).marker_id ? reviewCalls(items) : items;
  }

  private async alignmentPage(): Promise<ReviewPage> {
    const body = markerScoreRequest(
      reviewFeedFilters(this.feed),
      reviewCell(this.feed),
    );
    const { data, error } = await apiClient.POST("/v1/alignment:score", {
      body: { ...body, page_token: this.token },
    });
    if (error) throw new ApiRequestError(error);
    const items = (data.disagreements ?? []).map((row) => {
      const id = row.machine_answer_id ?? row.human_answer_id;
      if (!id) throw new Error("This comparison has no answer to open.");
      return { id, transcript_id: row.transcript_id, marker_id: row.marker_id };
    });
    return { items, next_page_token: data.next_page_token ?? null };
  }

  private async loadPage(): Promise<void> {
    if (this.loading) {
      return this.loading;
    }
    this.loading = (async () => {
      const page = reviewCell(this.feed)
        ? await this.alignmentPage()
        : await fetchAnswerPage(reviewFeedFilters(this.feed), this.token);
      const ids = new Set(this.items.map((item) => item.id));
      this.items = this.calls([
        ...this.items,
        ...page.items.filter((item) => !ids.has(item.id)),
      ]);
      this.token = page.next_page_token ?? undefined;
      this.initialized = true;
    })();
    try {
      await this.loading;
    } finally {
      this.loading = undefined;
    }
  }

  async eligible(answer: ReviewAnswer): Promise<boolean> {
    const filters = reviewFeedFilters(this.feed);
    if (!filters.needs_review) {
      return true;
    }
    // Fresh server-side eligibility, including org/team access and all feed
    // filters. An immutable Answer Get cannot tell whether a Label now exists.
    let token: string | undefined;
    do {
      const page = await fetchAnswerPage(
        {
          ...filters,
          marker_id: answer.marker_id,
          transcript_id: answer.transcript_id,
        },
        token,
      );
      if (page.items.some((item) => item.id === answer.id)) {
        return true;
      }
      token = page.next_page_token ?? undefined;
    } while (token);
    return false;
  }

  async entry(answer: ReviewAnswer): Promise<ReviewAnswer | null> {
    await this.initialize(answer.id, answer.transcript_id);
    if (this.aliases.has(answer.id) || (await this.eligible(answer))) {
      return answer;
    }
    return this.adjacent(answer.id, 1);
  }

  async adjacent(
    answerId: string,
    direction: -1 | 1,
  ): Promise<ReviewAnswer | null> {
    await this.initialize(answerId);
    // ASSUMED(A stale refreshed deep link resumes at the first eligible row.)
    let index = this.index(answerId) + direction;
    while (index >= 0) {
      if (index >= this.items.length) {
        if (direction === -1 || !this.token) {
          return null;
        }
        await this.loadPage();
        continue;
      }
      const candidate = this.items[index];
      if (candidate && (await this.eligible(candidate))) {
        return candidate;
      }
      index += direction;
    }
    return null;
  }

  async prefetchNext(answerId: string): Promise<void> {
    const next = await this.adjacent(answerId, 1);
    if (!next) {
      return;
    }
    await this.client.prefetchQuery({
      queryKey: queryKeys.transcripts.detail(next.transcript_id),
      queryFn: async () => {
        const { data, error } = await apiClient.GET("/v1/transcripts/{id}", {
          params: { path: { id: next.transcript_id } },
        });
        if (error) {
          throw new ApiRequestError(error);
        }
        return data;
      },
    });
  }
}

const selections = new WeakMap<QueryClient, Map<string, ReviewSelection>>();

export function reviewSelection(
  client: QueryClient,
  feed: string,
): ReviewSelection {
  let byFeed = selections.get(client);
  if (!byFeed) {
    byFeed = new Map();
    selections.set(client, byFeed);
  }
  let selection = byFeed.get(feed);
  if (!selection) {
    selection = new ReviewSelection(client, feed);
    byFeed.set(feed, selection);
  }
  return selection;
}
